import type { ApiSchema } from "../api/apiSchema";
import type { ProblemDetails } from "../api/http/problemDetails";
import type { UiError } from "./uiError";

/**
 * Problem codes of the DMW server (`server/src/dmw/kernel/errors.py`, `ErrorCode`) that the
 * web app has its own wording for. Each has `errors.problem.<code>.title` and `.message`
 * in every locale; the locale test checks that. Other codes get a generic text that names
 * the code, so a new server code is never mistaken for a known one.
 */
export const DESCRIBED_PROBLEM_CODES = [
  "internal_error",
  "configuration_invalid",
  "dependency_unavailable",
  "invalid_identifier",
  "invalid_version_reference",
  "illegal_state_transition",
  "request_validation_failed",
  "malformed_request",
  "route_not_found",
  "method_not_allowed",
  "resource_not_found",
  "resource_already_exists",
  "invalid_value",
  "authentication_required",
  "invalid_credentials",
  "account_disabled",
  "permission_denied",
  "csrf_token_invalid",
  "last_project_administrator",
  "last_administrator",
  "group_order_conflict",
  "first_administrator_setup_closed",
  "account_pending",
  "self_registration_closed",
  "email_link_invalid",
  "email_link_conflict",
  "project_creation_not_allowed",
  "project_limit_reached",
  "project_restore_period_running",
  "project_purge_unavailable",
  "rate_limited",
  "content_version_conflict",
  "corpus_import_invalid",
  "segmentation_configuration_invalid",
  "content_limit_exceeded",
  "stored_content_invalid",
  "content_store_failed",
  "connector_limit_reached",
  "definition_invalid",
  "review_decision_stale",
  "review_approvals_missing",
  "review_acknowledgement_required",
  "merge_blocked_by_shape_violations",
  "acceptance_blocked_by_checks",
  "dataset_version_conflict",
  "dataset_member_invalid",
  "dataset_checks_failed",
  "ontology_release_checks_failed",
  "dataset_version_unchecked",
  "export_target_failed",
  "stored_data_invalid",
  "thread_log_conflict",
  "idempotency_key_conflict",
  "thread_not_live",
  "thread_busy",
  "invalid_fork_point",
  "capability_mismatch",
  "context_limit_exceeded",
  "secret_detected",
  "output_invalid",
  "thread_log_invalid",
  "connector_offline",
  "connector_lost",
  "connector_busy",
  "agent_unavailable",
  "model_unavailable",
  "permission_profile_unknown",
  "agent_session_unavailable",
  "agent_failed",
  "turn_interrupted",
  "turn_timed_out",
  "provider_rate_limited",
  "provider_request_failed",
  "tool_not_allowed",
  "tool_call_limit_exceeded",
  "tool_consent_required",
  "tool_failed",
  // Thread and tool turns (live round, 29 Oct): a plain sentence, the next action, details folded.
  "connector_revoked",
  "skill_server_unavailable",
  "provider_authentication_failed",
  "provider_insufficient_credits",
  "tool_not_reviewed",
  "tool_runtime_unavailable",
  "tool_timed_out",
  "tool_output_too_large",
  "item_tools_denied",
  "workspace_access_denied",
  "tool_input_unavailable",
  "tool_environment_failed",
  "tool_model_unavailable",
  "tool_output_upload_failed",
] as const satisfies readonly ServerProblemCode[];

type DescribedProblemCode = (typeof DESCRIBED_PROBLEM_CODES)[number];

/** Every code the server may answer with: its one registry, `ErrorCode`, through the OpenAPI schema. */
type ServerProblemCode = ApiSchema<"Problem">["code"];

/**
 * Server codes the app has no wording of its own for yet: they show the generic text that
 * names the code. A ratchet (rule ratchet-baselines): this list only shrinks. A new server
 * code fails the build until it is described above or listed here with its reason.
 */
export const UNDESCRIBED_PROBLEM_CODES = [
  // Reach no person in the web app: the server's own start-up check, and the connector protocol.
  "state_machine_definition_invalid",
  "protocol_message_invalid",
  "message_type_unsupported",
  "protocol_version_unsupported",
  "turn_input_digest_mismatch",
  "no_active_turn",
  "permission_answer_impossible",
  // Content and data model changes, still to word with their pages.
  "capture_conflict",
  "companion_edit_unsupported",
  "companion_publication_unknown",
  "change_set_merge_conflict",
  "rdf_syntax_invalid",
  "merge_blocked_by_inconsistency",
  "item_retired",
  "item_withdrawn",
  // Releases, exports and ResearchSpace: administrators' pages, still to word.
  "correction_base_stale",
  "export_target_changed",
  "export_interrupted",
  "researchspace_not_configured",
  "researchspace_not_enabled",
  "researchspace_projects_conflict",
  "researchspace_configuration_failed",
  "researchspace_unavailable",
  "researchspace_interrupted",
] as const satisfies readonly ServerProblemCode[];

/** The compiler's check that both lists together cover every server code, and don't overlap. */
type Unlisted = Exclude<ServerProblemCode, DescribedProblemCode | (typeof UNDESCRIBED_PROBLEM_CODES)[number]>;
type ListedTwice = Extract<DescribedProblemCode, (typeof UNDESCRIBED_PROBLEM_CODES)[number]>;
export const EVERY_SERVER_CODE_LISTED: [Unlisted, ListedTwice] extends [never, never] ? true : { unlisted: Unlisted; listedTwice: ListedTwice } = true;

/**
 * Kinds of record (`details.resource_kind` of `resource_not_found`) whose absence has its
 * own message, `errors.problem.resource_not_found.resourceKinds.<kind>`, because the
 * general one ("check the link") does not say what to do.
 */
export const RESOURCE_KINDS_WITH_OWN_NOT_FOUND_TEXT = ["device_authorization", "connector", "thread", "entry", "turn", "host_offer", "ontology_term"] as const;

/**
 * Refusals whose `details.reason` changes what to do: each has its own title and text,
 * `errors.problem.<code>.reasons.<reason>.title` and `.message`, before any kind-of-record
 * text. Any other reason keeps its code's text.
 */
export const REASONS_WITH_OWN_TEXT = {
  // A library's item, which the project derives instead of replacing (ADR-0037 D7, J2-09).
  resource_not_found: ["held_by_library"],
  // A connector older than the tool needs: the one remedy is an update on that PC (PK2-T1).
  tool_runtime_unavailable: ["connector_outdated"],
  // A turn ended because its host may no longer read the project, not the reader's role (SOT-01b).
  permission_denied: ["project_access_ended"],
} as const satisfies Partial<Record<DescribedProblemCode, readonly string[]>>;

type ReasonCode = keyof typeof REASONS_WITH_OWN_TEXT;

/**
 * Codes whose server explanation (`detail`, in English) says what to fix, such as the line of
 * an import or the rule a password breaks. It is shown as technical detail under the text.
 */
const PROBLEM_CODES_WITH_HELPFUL_DETAIL: ReadonlySet<string> = new Set<DescribedProblemCode>([
  // The title is generic; the server names the service: the database, the sign-in engine, B's checks.
  "dependency_unavailable",
  // The server names the conflict and its remedy, such as revoking an existing server key (F124).
  "resource_already_exists",
  "invalid_value",
  "request_validation_failed",
  "corpus_import_invalid",
  "segmentation_configuration_invalid",
  "content_limit_exceeded",
  "connector_limit_reached",
  "project_limit_reached",
  "definition_invalid",
  "capability_mismatch",
  "context_limit_exceeded",
  "secret_detected",
  "output_invalid",
  "agent_failed",
  // The connector says why: not logged in, adapter missing, chosen permission mode not offered (F-2).
  "agent_unavailable",
  "provider_request_failed",
  // The connector names the command that approves the tool version on the host's PC.
  "tool_consent_required",
  // The tool's own message (F-2): technical, so it is folded behind Details.
  "tool_failed",
  // The connector's or server's plain reason, which says which of two causes it was (Sol N1, N5).
  "item_tools_denied",
  "tool_timed_out",
  // What the connector or tool says went wrong: named in the text, folded behind Details.
  "tool_environment_failed",
  "tool_input_unavailable",
  "tool_model_unavailable",
  "tool_output_too_large",
  "tool_output_upload_failed",
  "tool_runtime_unavailable",
]);

/** Codes whose server explanation is technical: shown folded behind Details, not in the main text. */
const PROBLEM_CODES_WITH_TECHNICAL_DETAIL: ReadonlySet<string> = new Set<DescribedProblemCode>([
  "tool_failed",
  "tool_environment_failed",
  "tool_input_unavailable",
  "tool_model_unavailable",
  "tool_output_too_large",
  "tool_output_upload_failed",
  "tool_runtime_unavailable",
]);

/** Codes where trying again can help: the cause is temporary or on the server's side. */
const RETRYABLE_PROBLEM_CODES: ReadonlySet<string> = new Set<DescribedProblemCode>([
  "internal_error",
  "dependency_unavailable",
  "thread_log_conflict",
  "connector_busy",
  "provider_rate_limited",
  "skill_server_unavailable",
  "tool_output_upload_failed",
]);

export interface UiErrorDescription {
  readonly titleKey: string;
  readonly messageKey: string;
  readonly messageValues: Readonly<Record<string, string | number>>;
  readonly tone: "error" | "warning";
  /** Offer "Try again" when the caller can retry. */
  readonly retryable: boolean;
  /** Quote this when reporting the problem; it is in every server log line. */
  readonly requestId: string | null;
  /** The server's own explanation, in English, where it helps the user fix the input. */
  readonly technicalDetail: string | null;
  /** The explanation is technical, such as a tool's own message: it waits behind Details. */
  readonly technicalDetailFolded?: boolean;
  /** A project the text names as `{{project}}`, by its ID: shown by its name (J2-09). */
  readonly namedProject?: string;
  /** The PC the text names as `{{pc}}`, by its connector's ID: shown by its name where it is the reader's own (TA-4). */
  readonly namedPc?: string;
}

/** What the user is told about an error, as i18n keys. */
export function describeUiError(error: UiError): UiErrorDescription {
  switch (error.kind) {
    case "problem": {
      const { code, requestId, status, detail, details } = error.problem;
      const described = isDescribedProblemCode(code);
      const reason = ownReason(error.problem);
      const project = details["owning_project_id"];
      const connector = details["connector_id"];
      return {
        titleKey: reason !== null ? `${reason}.title` : described ? `errors.problem.${code}.title` : "errors.problem.unknown.title",
        messageKey: reason !== null ? `${reason}.message` : described ? problemMessageKey(error.problem) : "errors.problem.unknown.message",
        messageValues: { code, status },
        tone: status >= 500 ? "error" : "warning",
        retryable: RETRYABLE_PROBLEM_CODES.has(code),
        requestId: requestId === "" ? null : requestId,
        technicalDetail: PROBLEM_CODES_WITH_HELPFUL_DETAIL.has(code) && detail !== "" ? detail : null,
        technicalDetailFolded: PROBLEM_CODES_WITH_TECHNICAL_DETAIL.has(code),
        ...(reason !== null && typeof project === "string" ? { namedProject: project } : {}),
        ...(reason !== null && typeof connector === "string" ? { namedPc: connector } : {}),
      };
    }
    case "serverUnreachable":
      return simpleDescription("serverUnreachable", "error", true);
    case "unexpectedResponse":
      return {
        ...simpleDescription("unexpectedResponse", "error", true),
        messageValues: { status: error.status },
      };
    case "appOutdated":
      // Retrying can't help; `ErrorNotice` offers the reload instead.
      return simpleDescription("appOutdated", "warning", false);
    case "invalidAddress":
      return {
        ...simpleDescription("invalidAddress", "warning", false),
        messageValues: { address: error.address },
      };
    case "unexpected":
      return simpleDescription("unexpected", "error", true);
  }
}

function simpleDescription(
  kind: Exclude<UiError["kind"], "problem">,
  tone: UiErrorDescription["tone"],
  retryable: boolean,
): UiErrorDescription {
  return {
    titleKey: `errors.${kind}.title`,
    messageKey: `errors.${kind}.message`,
    messageValues: {},
    tone,
    retryable,
    requestId: null,
    technicalDetail: null,
  };
}

function problemMessageKey(problem: ProblemDetails): string {
  const resourceKind = problem.details["resource_kind"];
  const ownText = RESOURCE_KINDS_WITH_OWN_NOT_FOUND_TEXT.find((kind) => kind === resourceKind);
  return problem.code === "resource_not_found" && ownText !== undefined
    ? `errors.problem.resource_not_found.resourceKinds.${ownText}`
    : `errors.problem.${problem.code}.message`;
}

/** The i18n key prefix of a refusal's own reason text, `errors.problem.<code>.reasons.<reason>`; `null` for any other. */
function ownReason(problem: ProblemDetails): string | null {
  const reason = problem.details["reason"];
  if (!(problem.code in REASONS_WITH_OWN_TEXT) || typeof reason !== "string") return null;
  const listed: readonly string[] = REASONS_WITH_OWN_TEXT[problem.code as ReasonCode];
  return listed.includes(reason) ? `errors.problem.${problem.code}.reasons.${reason}` : null;
}

function isDescribedProblemCode(code: string): code is DescribedProblemCode {
  return (DESCRIBED_PROBLEM_CODES as readonly string[]).includes(code);
}
